import { Form } from '@components/ui/form';
import { useExternalScheduleForm } from '@forms/schedules/external';
import { RelatedScheduleFormProps } from '../types';
import { useEffect } from 'react';
import { parseISO } from 'date-fns';
import { useRemoveExternalSchedule } from '@services/schedules/external/remove';
import { RemoveScheduleDialog } from '../../../remove';
import { useRescheduler } from '@contexts/rescheduler';
import { useTranslation } from 'react-i18next';
import { useToast } from '@hooks/useToast';

export const RelatedExternalScheduleForm = ({ children, schedule }: RelatedScheduleFormProps) => {
  const { remove, setState, appendSavedSchedule } = useRescheduler();
  const { t } = useTranslation('app', { keyPrefix: 'messages.success' });
  const { toast } = useToast();

  const { id, task, startsAt, status, contact, saved } = schedule;
  const taskId = task?.id || '';
  const jobId = task?.job?.id || '';

  const { mutate, isLoading } = useRemoveExternalSchedule({
    id,
    jobId,
    taskId,
    callback: () => {
      appendSavedSchedule(schedule);
      setState((prev) => ({
        ...prev,
        relatedSchedules: prev.relatedSchedules.filter((el) => el.id !== id),
      }));
      toast({
        title: t('schedule.remove'),
      });
    },
  });

  const { form, onSubmit } = useExternalScheduleForm({
    schedule,
    scheduleId: id,
    jobId,
    taskId,
    successHandler: (resp) => {
      if (!resp) return;
      const { data: updatedSchedule } = resp;
      appendSavedSchedule(updatedSchedule);
      setState((prev) => ({
        ...prev,
        relatedSchedules: prev.relatedSchedules.map((el) =>
          el.task?.id === updatedSchedule.task?.id ? updatedSchedule : el,
        ),
      }));
      toast({
        title: t('schedule.update'),
      });
    },
  });

  const { setValue } = form;

  useEffect(() => {
    setValue('date', parseISO(startsAt));
    setValue('time', parseISO(startsAt));
    setValue('taskId', task?.id || '');
    setValue('confirmed', status === 'approved');
    setValue('externalContactId', contact?.id || '');
  }, [task?.id, status, contact?.id, startsAt, saved, setValue]);

  return (
    <>
      <Form {...form}>
        <form
          className="flex flex-col gap-4"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(onSubmit)}>
          {children}
        </form>
      </Form>
      <RemoveScheduleDialog
        open={remove === id}
        setOpen={(v) => setState((prev) => ({ ...prev, remove: v ? id : '' }))}
        onConfirm={mutate}
        disabled={isLoading}
      />
    </>
  );
};
